/**
 * Quick Actions Modal Component - Detachable Admin Module
 * 
 * This component provides quick action modals for credit and plan management.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React, { useState } from 'react';
import { useAdminData } from '../hooks/useAdminData';
import { formatCredits, getPlanInfo } from '../utils';
import ChangePlanModal from './ChangePlanModal';
import type { CreditType, PlanType, PlatformUser } from '../types';

interface QuickActionsModalProps {
  user: PlatformUser | null;
  action: 'addCredits' | 'changePlan' | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal component for quick credit and plan management actions
 */
export const QuickActionsModal: React.FC<QuickActionsModalProps> = ({
  user,
  action,
  onClose,
  onSuccess
}) => {
  const { operations } = useAdminData();
  
  // Form state
  const [creditForm, setCreditForm] = useState({
    amount: '',
    creditType: 'free_platform' as CreditType,
    description: ''
  });
  
  // State for showing change plan modal
  const [showChangePlan, setShowChangePlan] = useState(false);

  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const amount = parseFloat(creditForm.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }

    const success = await operations.addCredits(
      user.id || user.email,
      amount,
      creditForm.creditType,
      creditForm.description || undefined
    );

    if (success) {
      setCreditForm({ amount: '', creditType: 'free_platform' as CreditType, description: '' });
      onSuccess();
      onClose();
    }
  };

  const handleChangePlanSuccess = () => {
    onSuccess();
    onClose();
  };

  const handleClose = () => {
    operations.clearError();
    onClose();
  };

  if (!action || !user) return null;

  // Handle change plan action with dedicated modal
  if (action === 'changePlan') {
    return (
      <ChangePlanModal
        isOpen={true}
        onClose={onClose}
        onSuccess={handleChangePlanSuccess}
        userIdentifier={user.id || user.email}
        currentPlan={user.billing.plan_type}
        currentMonthlyAllowance={user.billing.monthly_allowance}
        userDisplayName={user.firstName || user.email}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Add Credits Modal */}
          {action === 'addCredits' && (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Credits to {user.firstName || user.email}
              </h3>
              
              <form onSubmit={handleAddCredits} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="10000"
                    value={creditForm.amount}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    required
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Type
                  </label>
                  <select
                    value={creditForm.creditType}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, creditType: e.target.value as CreditType }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="free_platform">Free Platform Credits</option>
                    <option value="monthly">Monthly Credits</option>
                    <option value="paid_platform">Paid Platform Credits</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={creditForm.description}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Reason for credit grant..."
                  />
                </div>
                
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Current Balance:</strong> {formatCredits(user.billing.total_credits)}
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    User will receive the credits immediately after confirmation.
                  </p>
                </div>
                
                {operations.error && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {operations.error}
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={operations.loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {operations.loading ? 'Adding...' : 'Add Credits'}
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default QuickActionsModal;
